import { Component, Fragment } from 'preact';
import { createPortal } from 'preact/compat';
import { toast } from 'sonner';
import MarkdownNotes from './MarkdownNotes';
import MarkdownEditor from './MarkdownEditor';
import { CommentRow, CommentComposer, continuesPrevious } from './comments/CommentThread';
import RuneSpinner from './RuneSpinner';
import ElementIcon from './deckStats/ElementIcon';
import {
  fetchDeckComments, postDeckComment, deleteDeckComment, toggleDeckCommentLike,
} from '../utils/arena/deckLibraryApi';
import { playUI, UI } from '../utils/arena/uiSounds';
import AvatarImage from './ui/AvatarImage';
import CardLinkPreview, { cardLinkAnchorRect } from './CardLinkPreview';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, PARCHMENT,
  BEVELED_BTN, GOLD_BTN, INPUT_STYLE, CONTENT_BG_DEFAULT,
  FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { t, k } from '../localization/t';

// Solid base layer behind the texture wash. The shared PANEL_BG token
// is rgba(.92), which left the AAA panel slightly see-through against
// the deck builder behind it; this hex matches the deck-tile bg used
// elsewhere and reads as fully opaque.
const NOTES_PANEL_BASE = '#0c0a08';

// Inline skeleton primitive. Wraps the shared .skeleton-block CSS
// keyframes. Sizing comes from inline style so callers stay in cqw.
function SkeletonBlock({ width, height, circle = false, style }) {
  return (
    <span
      aria-hidden
      className={`skeleton-block${circle ? ' skeleton-circle' : ''}`}
      style={{ display: 'block', width, height, ...style }}
    />
  );
}

// Locate a literal text run inside a rendered container by walking
// every text node and looking for the substring in their concatenated
// values. Used to cross-link a comment's quoted_text back to its
// origin in the strategy notes. Click a quote in the comments
// panel, find where that exact text lives in the markdown render,
// build a Range over it. Returns null if the text can no longer be
// found (notes were edited after the quote was captured, etc).
function findRangeForText(container, target) {
  if (!container || !target) return null;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  const entries = [];
  let combined = '';
  while (walker.nextNode()) {
    const node = walker.currentNode;
    entries.push({ node, start: combined.length });
    combined += node.nodeValue || '';
  }
  const idx = combined.indexOf(target);
  if (idx === -1) return null;
  const endIdx = idx + target.length;
  let startNode = null, startOffset = 0;
  let endNode = null, endOffset = 0;
  for (const entry of entries) {
    const nodeText = entry.node.nodeValue || '';
    const nodeEnd = entry.start + nodeText.length;
    if (startNode === null && idx >= entry.start && idx < nodeEnd) {
      startNode = entry.node;
      startOffset = idx - entry.start;
    }
    if (endIdx > entry.start && endIdx <= nodeEnd) {
      endNode = entry.node;
      endOffset = endIdx - entry.start;
      break;
    }
  }
  if (!startNode || !endNode) return null;
  try {
    const range = document.createRange();
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
    return range;
  } catch {
    return null;
  }
}

// Single comment-card placeholder. Matches the real card's layout so
// the row doesn't reflow when the data lands. Avatar + name + body
// lines, sized to the same metrics as renderCommentCard.
function CommentCardSkeleton() {
  return (
    <div style={{ padding: '0.5cqw 0.85cqw' }}>
      <div className="flex items-start gap-2">
        <SkeletonBlock width="1.55cqw" height="1.55cqw" circle />
        <div className="flex-1 min-w-0">
          <SkeletonBlock width="35%" height="0.62cqw" />
          <SkeletonBlock width="100%" height="0.55cqw" style={{ marginTop: '0.45cqw' }} />
          <SkeletonBlock width="72%" height="0.55cqw" style={{ marginTop: '0.3cqw' }} />
        </div>
      </div>
    </div>
  );
}

const STRATEGY_PLACEHOLDER_LINES = [
  k('## Game Plan'),
  k('Brief overview of how this deck wants to win.'),
  '',
  k('## Mulligan'),
  k('- Keep openers with...'),
  k('- Mulligan when...'),
  '',
  k('## Matchups'),
  k('- vs. aggro: ...'),
  k('- vs. control: ...'),
];

const ENTRY_DURATION_MS = 260;
const EXIT_DURATION_MS = 200;

// Centered AAA-style overlay that hosts the deck's strategy notes and
// (when shared) per-deck comments side-by-side. Uses the same texture
// wash + gold-cornered framing as the First Player dice modal and the
// pre-con deck picker so it reads as a first-class game UI surface
// rather than a generic side rail.
export default class DeckSidePanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      strategyDraft: props.strategyNotes || '',
      strategyEditing: false,
      strategySaving: false,
      comments: [],
      commentsLoading: false,
      commentDraft: '',
      commentPosting: false,
      commentSort: 'recent',
      commentLikeBusy: null,
      hoveredCommentId: null,
      showSortMenu: false,
      entered: false,
      exiting: false,
      cardHover: null,
      // Live text selection inside the strategy read view. Populated
      // by selectionchange while the panel is open; gates the
      // floating "Comment on selection" button.
      selectionAnchor: null,
      // Quote that's been pinned to the composer (user clicked the
      // floating button). Cleared after posting or via the X button.
      quotedTextDraft: null,
      // Set of comment ids whose author has a resolved crown title.
      // PlayerTitle resolves asynchronously, so we track it here to
      // know when to append a trailing comma to the name (the title
      // itself renders on its own row below).
      commentIdsWithTitle: new Set(),
      // Cross-link state for comment quotes → strategy notes. Hover
      // previews a highlight on the left; click pins it (and scrolls
      // the strategy view to it). Displayed highlight is
      // pinnedQuote || previewQuote.
      previewQuote: null,
      pinnedQuote: null,
      // Comment the panel was deep-linked to. Set once its row is in
      // the loaded list, scrolled into view, and cleared after a beat.
      highlightedCommentId: null,
    };
    this.strategyReadRef = null;
    this._entryRaf = null;
    this._exitTimer = null;
    this._quotePinTimer = null;
    this._highlightTimer = null;
    // Which highlightCommentId has already been applied, so a comment
    // reload (sort change) doesn't re-run the scroll + flash.
    this._appliedHighlightId = null;
  }

  componentDidMount() {
    if (typeof document !== 'undefined') {
      document.addEventListener('selectionchange', this.handleSelectionChange);
    }
    if (this.props.open) {
      this.runEntrySequence();
      this._escDismissId = pushDismiss(() => this.beginExit());
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.highlightCommentId !== prevProps.highlightCommentId) {
      this._appliedHighlightId = null;
    }
    const hl = this.props.highlightCommentId;
    if (hl && hl !== this._appliedHighlightId && !this.state.commentsLoading && this.state.comments.length) {
      this.applyCommentHighlight(hl);
    }
    // Re-opening the panel must reset the entry/exit phase, otherwise
    // a stale `exiting: true` left over from the previous close keeps
    // the backdrop invisible AND non-dismissible. The entire UI
    // appears to freeze until the renderer is reloaded.
    if (!prevProps.open && this.props.open) {
      this.runEntrySequence();
      this._escDismissId = pushDismiss(() => this.beginExit());
    } else if (prevProps.open && !this.props.open) {
      removeDismiss(this._escDismissId);
    }
    if (prevProps.strategyNotes !== this.props.strategyNotes && !this.state.strategyEditing) {
      this.setState({ strategyDraft: this.props.strategyNotes || '' });
    }
    if (!prevProps.commentsEnabled && this.props.commentsEnabled) {
      this.loadComments();
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._entryRaf) cancelAnimationFrame(this._entryRaf);
    if (this._exitTimer) clearTimeout(this._exitTimer);
    if (this._quotePinTimer) clearTimeout(this._quotePinTimer);
    if (this._highlightTimer) clearTimeout(this._highlightTimer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('selectionchange', this.handleSelectionChange);
    }
    removeDismiss(this._escDismissId);
  }

  // Scroll the deep-linked comment into view and flash it. Runs once per
  // highlightCommentId after the comments list has loaded. A comment
  // that is no longer in the list (deleted) is ignored silently.
  applyCommentHighlight = (id) => {
    this._appliedHighlightId = id;
    if (!this.state.comments.some((c) => c.id === id)) return;
    if (this._highlightTimer) clearTimeout(this._highlightTimer);
    this.setState({ highlightedCommentId: id }, () => {
      const el = this.base?.querySelector?.(`[data-comment-id="${id}"]`);
      el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    });
    this._highlightTimer = setTimeout(() => {
      this._highlightTimer = null;
      if (!this._unmounted) this.setState({ highlightedCommentId: null });
    }, 3500);
  };

  // Watches document selection while the panel is open. When the
  // selection lives inside the strategy read view (rendered via
  // MarkdownNotes) and isn't collapsed, we capture the text + the
  // selection's bounding rect (in canvas-local coords) so the
  // floating "Comment on selection" button has somewhere to anchor.
  handleSelectionChange = () => {
    if (!this.props.open) return;
    // strategyReadRef might be a stale pointer to a div that has
    // since unmounted (e.g. switching from read mode to edit mode);
    // the document.contains guard avoids tripping the button when
    // its anchor element is no longer on screen.
    if (!this.strategyReadRef || !document.contains(this.strategyReadRef)) {
      if (this.state.selectionAnchor) this.setState({ selectionAnchor: null });
      return;
    }
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
      if (this.state.selectionAnchor) this.setState({ selectionAnchor: null });
      return;
    }
    const range = sel.getRangeAt(0);
    const anchorInside = this.strategyReadRef.contains(range.startContainer)
      && this.strategyReadRef.contains(range.endContainer);
    if (!anchorInside) {
      if (this.state.selectionAnchor) this.setState({ selectionAnchor: null });
      return;
    }
    const text = sel.toString().trim();
    if (!text) {
      if (this.state.selectionAnchor) this.setState({ selectionAnchor: null });
      return;
    }
    const viewportRect = range.getBoundingClientRect();
    const canvasEl = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
    const canvasRect = canvasEl
      ? canvasEl.getBoundingClientRect()
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
    this.setState({
      selectionAnchor: {
        text,
        rect: {
          left: viewportRect.left - canvasRect.left,
          right: viewportRect.right - canvasRect.left,
          top: viewportRect.top - canvasRect.top,
          bottom: viewportRect.bottom - canvasRect.top,
          canvasWidth: canvasRect.width,
          canvasHeight: canvasRect.height,
        },
      },
    });
  };

  startCommentFromSelection = () => {
    const { selectionAnchor } = this.state;
    if (!selectionAnchor || !this.props.commentsEnabled) return;
    // Clear the document selection so the floating button hides and
    // doesn't trail behind the composer.
    if (typeof window !== 'undefined') {
      const sel = window.getSelection();
      if (sel && sel.removeAllRanges) sel.removeAllRanges();
    }
    this.setState({ quotedTextDraft: selectionAnchor.text, selectionAnchor: null });
  };

  clearQuotedDraft = () => {
    if (this.state.quotedTextDraft) this.setState({ quotedTextDraft: null });
  };

  // Cancel any in-flight enter/exit timers, snap the panel back to a
  // pre-entry state, and schedule the entry animation. Loads comments
  // fresh on every open so a deck shared between sessions reflects
  // any new comments without a full reload.
  runEntrySequence = () => {
    if (this._exitTimer) {
      clearTimeout(this._exitTimer);
      this._exitTimer = null;
    }
    if (this._entryRaf) {
      cancelAnimationFrame(this._entryRaf);
      this._entryRaf = null;
    }
    if (this._quotePinTimer) {
      clearTimeout(this._quotePinTimer);
      this._quotePinTimer = null;
    }
    // Reset strategy editing on every open. Leaving editing mode
    // open across a close+reopen meant the user returned to the
    // panel in mid-write with a draft that may no longer reflect
    // the current notes. And the editor's autoFocus would land
    // the caret past the visible scroll edge until they typed. A
    // re-open always starts in the calmer read view; the user
    // clicks "Edit" again to resume.
    this.setState({
      entered: false,
      exiting: false,
      strategyEditing: false,
      strategyDraft: this.props.strategyNotes || '',
      pinnedQuote: null,
      previewQuote: null,
    });
    this._entryRaf = requestAnimationFrame(() => {
      this._entryRaf = requestAnimationFrame(() => {
        if (!this._unmounted) this.setState({ entered: true });
      });
    });
    if (this.props.commentsEnabled) this.loadComments();
  };

  loadComments = async () => {
    if (!this.props.deckId) return;
    this.setState({ commentsLoading: true });
    try {
      const comments = await fetchDeckComments(this.props.deckId, { sort: this.state.commentSort });
      this.setState({ comments: Array.isArray(comments) ? comments : [], commentsLoading: false });
    } catch (err) {
      console.error('[DeckSidePanel] Failed to load comments:', err);
      this.setState({ commentsLoading: false });
    }
  };

  setCommentSort = (sort) => {
    if (this.state.commentSort === sort) {
      this.setState({ showSortMenu: false });
      return;
    }
    this.setState({ commentSort: sort, showSortMenu: false }, this.loadComments);
  };

  handleToggleCommentLike = async (commentId) => {
    if (!this.props.deckId || this.state.commentLikeBusy === commentId) return;
    this.setState({ commentLikeBusy: commentId });
    try {
      const result = await toggleDeckCommentLike(this.props.deckId, commentId);
      this.setState((s) => ({
        commentLikeBusy: null,
        comments: s.comments.map((c) =>
          c.id === commentId ? { ...c, liked: !!result.liked, likes: result.likes || 0 } : c
        ),
      }));
    } catch (err) {
      this.setState({ commentLikeBusy: null });
      toast.error(t('Failed to like comment'));
    }
  };

  handleOpenCommenterProfile = (playerId) => {
    if (!playerId || !this.props.onOpenProfile) return;
    this.props.onOpenProfile(playerId);
  };

  // Resolve a player's chosen avatar (stored as a card unique_id) to
  // an image URL via sorceryCards. Returns null when no card is
  // chosen or the catalog isn't ready yet.
  getAvatarImageUrl = (cardId) => {
    if (!cardId || !Array.isArray(this.props.sorceryCards)) return null;
    const card = this.props.sorceryCards.find((c) => c.unique_id === cardId);
    return card?.printings?.[0]?.image_url || null;
  };

  // Animate out before unmounting. The parent flips `open` to false
  // after onClose; we apply the exit transform during the
  // EXIT_DURATION_MS window so the panel fades out smoothly.
  beginExit = () => {
    if (this.state.exiting || !this.props.open) return;
    playUI(UI.CANCEL);
    this.setState({ exiting: true });
    this._exitTimer = setTimeout(() => {
      this._exitTimer = null;
      if (this.props.onClose) this.props.onClose();
    }, EXIT_DURATION_MS);
  };

  handleStartEditStrategy = () => {
    if (!this.props.canEditStrategy) return;
    this.setState({ strategyEditing: true });
  };

  // Cross-link a comment's quoted_text back to its origin in the
  // strategy notes. Hover previews the highlight on the left;
  // click pins the highlight and scrolls it into view. Clicking the
  // same quote again toggles it off.
  handleQuoteHover = (quote) => {
    if (!quote) return;
    this.setState({ previewQuote: quote });
  };

  handleQuoteLeave = () => {
    if (this.state.previewQuote !== null) this.setState({ previewQuote: null });
  };

  handleQuoteClick = (quote) => {
    if (!quote) return;
    const togglingOff = this.state.pinnedQuote === quote;
    // Cancel any pending auto-clear from a previous pin. We either
    // arm a fresh timer below (new pin) or stop highlighting
    // entirely (toggle off).
    if (this._quotePinTimer) {
      clearTimeout(this._quotePinTimer);
      this._quotePinTimer = null;
    }
    // Exiting edit mode when needed. The highlight overlay only
    // renders inside the read view, so clicking a quote while in
    // edit mode would do nothing visible.
    const exitingEdit = this.state.strategyEditing;
    this.setState(
      {
        pinnedQuote: togglingOff ? null : quote,
        previewQuote: null,
        ...(exitingEdit
          ? { strategyEditing: false, strategyDraft: this.props.strategyNotes || '' }
          : null),
      },
      () => {
        if (togglingOff) return;
        this.scrollToQuote(quote);
        // Auto-clear the pin after ~3.5s. Long enough to read the
        // highlighted passage, short enough that the gold wash
        // doesn't linger as visual noise once the user has moved
        // on. The "x clicks to toggle off" behavior still works
        // during this window if the user wants to clear sooner.
        this._quotePinTimer = setTimeout(() => {
          this._quotePinTimer = null;
          if (this._unmounted) return;
          if (this.state.pinnedQuote === quote) {
            this.setState({ pinnedQuote: null });
          }
        }, 3500);
      },
    );
  };

  scrollToQuote = (quote) => {
    const container = this.strategyReadRef;
    if (!container || !quote) return;
    const range = findRangeForText(container, quote);
    if (!range) return;
    const rect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const pad = 12;
    if (rect.top < containerRect.top + pad) {
      container.scrollTop -= containerRect.top + pad - rect.top;
    } else if (rect.bottom > containerRect.bottom - pad) {
      container.scrollTop += rect.bottom - (containerRect.bottom - pad);
    }
  };

  handleCancelStrategy = () => {
    playUI(UI.CANCEL);
    this.setState({ strategyEditing: false, strategyDraft: this.props.strategyNotes || '' });
  };

  handleSaveStrategy = async () => {
    if (!this.props.canEditStrategy || !this.props.onSaveStrategy) return;
    this.setState({ strategySaving: true });
    try {
      await this.props.onSaveStrategy(this.state.strategyDraft);
      this.setState({ strategyEditing: false, strategySaving: false });
      playUI(UI.CONFIRM);
    } catch (err) {
      this.setState({ strategySaving: false });
      toast.error(t('Failed to save strategy notes'));
    }
  };

  handlePostComment = async () => {
    const body = this.state.commentDraft.trim();
    if (!body || !this.props.deckId) return;
    const quotedText = this.state.quotedTextDraft || undefined;
    this.setState({ commentPosting: true });
    try {
      const comment = await postDeckComment(this.props.deckId, body, { quotedText });
      this.setState((s) => ({
        // New comment goes to the top when sorting by Recent;
        // otherwise reload to pick up the right position. Top-prepend
        // is the common case so we treat the other sorts as a quiet
        // re-fetch on next interaction.
        comments: comment
          ? (s.commentSort === 'recent' ? [comment, ...s.comments] : [...s.comments, comment])
          : s.comments,
        commentDraft: '',
        commentPosting: false,
        quotedTextDraft: null,
      }));
      playUI(UI.CONFIRM);
    } catch (err) {
      this.setState({ commentPosting: false });
      toast.error(t('Failed to post comment'));
    }
  };

  handleDeleteComment = async (commentId) => {
    try {
      await deleteDeckComment(this.props.deckId, commentId);
      this.setState((s) => ({ comments: s.comments.filter((c) => c.id !== commentId) }));
    } catch (err) {
      toast.error(t('Failed to delete comment'));
    }
  };

  // ── Card-link plumbing ────────────────────────────────────
  //
  // Card pills inside the notes (strategy markdown OR comment bodies)
  // hand the unique_id up to the host through the parent's
  // onOpenCard prop, and use a local hover preview that floats the
  // card's primary image next to the pill.

  findCardById = (cardId) => {
    if (!cardId || !Array.isArray(this.props.sorceryCards)) return null;
    return this.props.sorceryCards.find((c) => c.unique_id === cardId) || null;
  };

  handleCardLinkClick = (cardId) => {
    if (!this.props.onOpenCard) return;
    const card = this.findCardById(cardId);
    if (!card) return;
    this.props.onOpenCard(card);
  };

  handleCardLinkHover = (cardId, element) => {
    const card = this.findCardById(cardId);
    const rect = cardLinkAnchorRect(element);
    if (!card || !rect) return;
    this.setState({ cardHover: { card, rect } });
  };

  handleCardLinkHoverEnd = () => {
    if (this.state.cardHover) this.setState({ cardHover: null });
  };

  // Panel header. Overline "Deck Notes" eyebrow, then a row with
  // author avatar + deck name + author name & tier, and a close X
  // anchored top-right. The avatar is clickable (opens the author's
  // profile) when the host wired an onOpenProfile handler.
  renderElementPill(element) {
    const colors = {
      Fire:  { color: '#FFB07A', bg: 'rgba(255,95,0,0.18)',  border: 'rgba(255,95,0,0.5)' },
      Water: { color: '#7FE9E9', bg: 'rgba(1,255,255,0.14)', border: 'rgba(1,255,255,0.45)' },
      Earth: { color: '#E0C18A', bg: 'rgba(207,165,114,0.18)', border: 'rgba(207,165,114,0.5)' },
      Air:   { color: '#C3D5EE', bg: 'rgba(160,186,219,0.18)', border: 'rgba(160,186,219,0.5)' },
    };
    const c = colors[element] || { color: TEXT_BODY, bg: 'rgba(180,140,60,0.12)', border: `${GOLD} 0.32)` };
    return (
      <span
        key={element}
        className="inline-flex items-center font-bold rounded-full"
        style={{
          gap: '0.3cqw',
          padding: '0.15cqw 0.55cqw 0.15cqw 0.4cqw',
          fontSize: '0.55cqw',
          letterSpacing: '0.1cqw',
          color: c.color,
          background: c.bg,
          border: `1px solid ${c.border}`,
 }}
 >
 <ElementIcon element={element} size="0.75cqw" />
 {t(element)}
 </span>
 );
 }

 renderHeader() {
 const { deckName, author, onOpenProfile, deckElements } = this.props;
 const authorName = author?.playerName || null;
 const authorId = author?.playerId || null;
 const avatarUrl = this.getAvatarImageUrl(author?.playerAvatar);
 const initials = (authorName || '?').slice(0, 1).toUpperCase();
 const canOpenProfile = !!onOpenProfile && !!authorId;
 // Tri-state: explicit `null` author means "still loading", so we
    // render skeleton placeholders for the avatar circle + name row.
    // `undefined` (prop not passed at all) falls through to render.
    const isAuthorPending = author === null;

    const hasElements = Array.isArray(deckElements) && deckElements.length > 0;

    return (
      <div className="relative shrink-0 mb-3">
        <div className="flex items-center gap-4">
          {/* Avatar bumped to 3.4cqw so it visually anchors three
              stacked rows on its right (deck name + author/title +
              elements) without looking pinched. */}
          {isAuthorPending ? (
            <SkeletonBlock width="3.4cqw" height="3.4cqw" circle />
          ) : (
            <button
              type="button"
              className="notes-profile-link shrink-0 overflow-hidden"
              style={{
                width: '3.4cqw',
                height: '3.4cqw',
                borderRadius: '50%',
                background: 'rgba(180, 140, 60, 0.12)',
                border: `1px solid ${GOLD} 0.5)`,
                padding: 0,
                boxShadow: `inset 0 0 0 1px ${GOLD} 0.18), 0 4px 12px rgba(0, 0, 0, 0.4)`,
              }}
              onClick={() => canOpenProfile && onOpenProfile(authorId)}
              disabled={!canOpenProfile}
              title={canOpenProfile ? t("Open {name}'s profile", { name: authorName || t('player') }) : undefined}
              aria-label={canOpenProfile ? t("Open {name}'s profile", { name: authorName || t('player') }) : undefined}
            >
              {avatarUrl ? (
                <AvatarImage src={avatarUrl} className="w-full h-full" />
              ) : (
                <span
                  className="arena-heading"
                  style={{
                    display: 'flex',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: ACCENT_GOLD,
                    fontSize: '1.4cqw',
                    fontWeight: 700,
                  }}
                >
                  {initials}
                </span>
              )}
            </button>
          )}

          <div className="flex-1 min-w-0 flex flex-col" style={{ gap: '0.32cqw' }}>
            {/* Deck name as the dominant headline. */}
            {deckName ? (
              <div
                className="arena-heading font-bold truncate"
                style={{
                  color: TEXT_PRIMARY,
                  fontSize: '1.2cqw',
                  letterSpacing: '0.02em',
                  lineHeight: 1.1,
                  textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                }}
                title={deckName}
              >
                {deckName}
              </div>
            ) : (
              <SkeletonBlock width="55%" height="1.2cqw" />
            )}

            {/* Author + title pill. Own row so the relationship
                "this deck was made by X (rank Y)" reads clearly. */}
            {isAuthorPending ? (
              <SkeletonBlock width="6cqw" height="0.65cqw" />
            ) : (
              <div className="flex items-center truncate" style={{ fontSize: '0.7cqw' }}>
                <button
                  type="button"
                  className="notes-profile-link"
                  onClick={() => canOpenProfile && onOpenProfile(authorId)}
                  disabled={!canOpenProfile}
                  style={{
                    color: ACCENT_GOLD,
                    fontWeight: 600,
                    background: 'transparent',
                    padding: 0,
                  }}
                  onMouseEnter={(e) => { if (canOpenProfile) e.currentTarget.style.color = '#f0c870'; }}
                  onMouseLeave={(e) => { if (canOpenProfile) e.currentTarget.style.color = ACCENT_GOLD; }}
                >
                  {authorName ? `@${authorName}` : t('Anonymous')}
                </button>
              </div>
            )}

            {/* Element pills on their own row. Separating them from
                the author line gives both a chance to breathe and
                lets up to 4 element chips fit at full readable
                size without wrapping. */}
            {hasElements ? (
              <div className="flex items-center" style={{ gap: '0.35cqw', flexWrap: 'wrap' }}>
                {deckElements.map((el) => this.renderElementPill(el))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  // Floating "💬 Comment on selection" pill anchored to the current
  // text selection inside the strategy read view. Only available
  // when the deck has comments enabled (the panel must actually
  // have somewhere to file the new comment).
  renderSelectionCommentButton() {
    const { selectionAnchor } = this.state;
    if (!selectionAnchor || !this.props.commentsEnabled) return null;
    const { rect } = selectionAnchor;
    const cqw = rect.canvasWidth / 100;
    // Approximate pill width (icon + text + padding). Centering on
    // the selection midpoint feels more natural than anchoring to
    // its right edge. The button always reads as "this is for the
    // text you just highlighted."
    const buttonWidthPx = 12 * cqw;
    const selectionCenter = (rect.left + rect.right) / 2;
    const left = Math.max(8, Math.min(selectionCenter - buttonWidthPx / 2, rect.canvasWidth - buttonWidthPx - 8));
    // Prefer above the selection; only fall back below if there's
    // literally no room (selection sits right at the canvas top).
    const popoverHeight = 2.4 * cqw;
    const topAbove = rect.top - popoverHeight - 0.4 * cqw;
    const placeAbove = topAbove >= 8;
    const top = placeAbove ? topAbove : rect.bottom + 0.4 * cqw;

    const node = (
      <button
        type="button"
        className="notes-profile-link inline-flex items-center gap-1.5 font-semibold"
        style={{
          position: 'fixed',
          top,
          left,
          zIndex: 280,
          padding: '0.45cqw 0.85cqw',
          background: 'linear-gradient(180deg, rgba(212,168,67,0.95) 0%, rgba(160,120,40,0.95) 100%)',
          border: '1px solid rgba(212,168,67,0.65)',
          borderRadius: 'var(--radius-full)',
          color: '#0e0a06',
          fontSize: '0.6cqw',
          boxShadow: '0 10px 24px rgba(0,0,0,0.45), 0 0 12px rgba(212,168,67,0.25)',
        }}
        // Use onMouseDown so the click fires before selectionchange
        // clears the selection and unmounts the button.
        onMouseDown={(e) => { e.preventDefault(); this.startCommentFromSelection(); }}
        data-sound={UI.CONFIRM}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.7cqw', height: '0.7cqw' }}>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
        <span>{t('Comment selection')}</span>
      </button>
    );

    const canvas = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
    return canvas ? createPortal(node, canvas) : node;
  }

  renderCardHoverPreview() {
    const { cardHover } = this.state;
    if (!cardHover) return null;
    return <CardLinkPreview card={cardHover.card} rect={cardHover.rect} />;
  }

  renderStrategyEdit() {
    const { strategyDraft, strategySaving } = this.state;
    return (
      <div className="flex flex-col gap-3 flex-1 min-h-0">
        <div
          className="flex-1 min-h-0 px-3 py-2 rounded overflow-hidden flex flex-col"
          style={{ ...INPUT_STYLE, borderRadius: 'var(--radius-md)' }}
        >
          <MarkdownEditor
            value={strategyDraft}
            onChange={(value) => this.setState({ strategyDraft: value })}
            placeholder={STRATEGY_PLACEHOLDER_LINES.map((line) => (line ? t(line) : line)).join('\n')}
            autoFocus
            style={{ color: TEXT_BODY }}
            sorceryCards={this.props.sorceryCards}
            onOpenCard={(cardId) => this.handleCardLinkClick(cardId)}
            onCardLinkHover={this.handleCardLinkHover}
            onCardLinkHoverEnd={this.handleCardLinkHoverEnd}
          />
        </div>
        <div className="flex gap-2 justify-end shrink-0">
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-semibold"
            style={{ ...BEVELED_BTN, borderRadius: 'var(--radius-md)', color: TEXT_MUTED }}
            onClick={this.handleCancelStrategy}
            disabled={strategySaving}
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            className="px-4 py-1.5 text-xs font-bold"
            style={{ ...GOLD_BTN, borderRadius: 'var(--radius-md)', opacity: strategySaving ? 0.5 : 1 }}
            onClick={this.handleSaveStrategy}
            disabled={strategySaving}
          >
            {strategySaving ? <RuneSpinner size={14} /> : t('Save Notes')}
          </button>
        </div>
      </div>
    );
  }

  // Skeleton placeholder for the strategy read view. Renders a
  // mix of heading-sized + paragraph-sized blocks so the shape
  // hints at "markdown notes are about to appear here." Triggered
  // when the parent passes strategyNotes as null/undefined (vs
  // an empty string, which is rendered as the empty state below).
  renderStrategyReadSkeleton() {
    return (
      <div className="flex flex-col flex-1 min-h-0 gap-2 pr-2 -mr-2 overflow-hidden">
        <SkeletonBlock width="55%" height="1.25cqw" />
        <SkeletonBlock width="94%" height="0.7cqw" style={{ marginTop: '0.4cqw' }} />
        <SkeletonBlock width="88%" height="0.7cqw" />
        <SkeletonBlock width="72%" height="0.7cqw" />
        <SkeletonBlock width="38%" height="1cqw" style={{ marginTop: '1cqw' }} />
        <SkeletonBlock width="80%" height="0.65cqw" />
        <SkeletonBlock width="62%" height="0.65cqw" />
        <SkeletonBlock width="35%" height="0.95cqw" style={{ marginTop: '1cqw' }} />
        <SkeletonBlock width="90%" height="0.65cqw" />
        <SkeletonBlock width="55%" height="0.65cqw" />
      </div>
    );
  }

  renderStrategyRead() {
    const { canEditStrategy, strategyNotes } = this.props;
    if (strategyNotes == null) {
      // tri-state: undefined/null → still loading; empty string falls
      // through to the empty-state branch below.
      return this.renderStrategyReadSkeleton();
    }
    const hasContent = !!(strategyNotes && strategyNotes.trim());

    if (!hasContent) {
      return (
        <div className="flex flex-col items-center justify-center flex-1 text-center px-4 py-8 gap-4">
          <div className="text-xs italic" style={{ color: TEXT_MUTED, maxWidth: '20cqw' }}>
            {canEditStrategy
              ? t('No strategy notes yet. Write down your game plan, mulligan rules, or matchup tips.')
              : t('The deck author hasn\'t added strategy notes for this deck.')}
          </div>
          {canEditStrategy && (
            <button
              type="button"
              className="px-5 py-2 text-xs font-bold"
              style={{ ...GOLD_BTN, borderRadius: 'var(--radius-md)' }}
              onClick={this.handleStartEditStrategy}
            >
              {t('Write Notes')}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div
          // Game-feel CSS in index.css disables text selection on
          // every element via a universal `*` rule. Setting
          // user-select on a parent isn't enough. Each child
          // resets back to none from the same rule. The
          // `.text-selectable` class re-enables selection on this
          // element AND every descendant via `.text-selectable *`
          // so any viewer (owner OR library reader) can highlight
          // a passage and trigger the floating Comment button.
          // Edit mode is unaffected. Contenteditable always
          // allows its own selection.
          className="flex-1 overflow-y-auto pr-2 -mr-2 text-selectable"
          ref={(el) => { this.strategyReadRef = el; }}
        >
          {/* The highlight overlay needs to scroll with the markdown
              content, so it lives inside this `position: relative`
              wrapper rather than being a sibling of the scroll
              container. Quote rects are computed against this
              wrapper's bounding rect, which moves naturally as the
              scroll position changes. */}
          <div
            className="relative"
            ref={(el) => { this.strategyContentRef = el; }}
          >
            <MarkdownNotes
              text={strategyNotes}
              onCardClick={this.handleCardLinkClick}
              onCardHover={this.handleCardLinkHover}
              onCardHoverEnd={this.handleCardLinkHoverEnd}
            />
            {this.renderQuoteHighlight()}
          </div>
        </div>
        {canEditStrategy && (
          <div className="flex justify-end pt-2 mt-2 shrink-0" style={{ borderTop: `1px solid ${GOLD} 0.1)` }}>
            <button
              type="button"
              className="px-3 py-1 text-[0.625cqw] font-semibold"
              style={{ ...BEVELED_BTN, borderRadius: 'var(--radius-md)', color: TEXT_MUTED }}
              onClick={this.handleStartEditStrategy}
            >
              {t('Edit')}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Draws the cross-link highlight inside the strategy notes when a
  // comment quote is hovered or clicked. Walks the content's text
  // nodes to find the exact substring, builds a Range over it, and
  // emits one positioned div per client rect (so multi-line quotes
  // get one highlight per visual line). Returns null when the
  // quote can't be found. The markdown might have changed since
  // the comment was posted, or the quote spans across structural
  // boundaries the renderer breaks into separate elements.
  renderQuoteHighlight() {
    const activeQuote = this.state.pinnedQuote || this.state.previewQuote;
    if (!activeQuote) return null;
    const wrapper = this.strategyContentRef;
    if (!wrapper) return null;
    const range = findRangeForText(wrapper, activeQuote);
    if (!range) return null;
    const rects = Array.from(range.getClientRects());
    if (rects.length === 0) return null;
    const wrapperRect = wrapper.getBoundingClientRect();
    const pinned = this.state.pinnedQuote === activeQuote;
    const bg = pinned ? 'rgba(212, 168, 67, 0.22)' : 'rgba(212, 168, 67, 0.12)';
    return rects.map((r, i) => (
      <div
        key={`quote-hl-${i}`}
        aria-hidden
        style={{
          position: 'absolute',
          left: `${r.left - wrapperRect.left}px`,
          top: `${r.top - wrapperRect.top}px`,
          width: `${r.width}px`,
          height: `${r.height}px`,
          background: bg,
          borderRadius: 'var(--radius-xs)',
          pointerEvents: 'none',
          transition: 'background 150ms ease',
        }}
      />
    ));
  }

  renderColumnHeader(label, accessory) {
    return (
      <div className="flex items-center justify-between shrink-0 mb-2.5">
        <span
          className="arena-heading font-bold"
          style={{
            color: ACCENT_GOLD,
            fontSize: '0.75cqw',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          }}
        >
          {label}
        </span>
        {accessory}
      </div>
    );
  }

  renderStrategyColumn() {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        {this.renderColumnHeader(t('Strategy'))}
        <OrnamentalDivider className="mb-3" />
        {this.state.strategyEditing ? this.renderStrategyEdit() : this.renderStrategyRead()}
      </div>
    );
  }

  // One comment, rendered by the shared comment surface. The only
  // deck-specific part is the quoted-text cross-link back into the
  // strategy notes, which rides in as the row's `quote` slot.
  renderCommentCard(c, { canDelete, isHovered, isLikeBusy, continued = false }) {
    return (
      <CommentRow
        key={c.id}
        comment={c}
        continued={continued}
        highlighted={c.id === this.state.highlightedCommentId}
        avatarUrl={this.getAvatarImageUrl(c.playerAvatar)}
        sorceryCards={this.props.sorceryCards}
        canDelete={canDelete}
        isHovered={isHovered}
        onHoverChange={(id) => this.setState((s) => (
          id === null ? (s.hoveredCommentId === c.id ? { hoveredCommentId: null } : null) : { hoveredCommentId: id }
        ))}
        likeBusy={isLikeBusy}
        onOpenProfile={this.props.onOpenProfile ? this.handleOpenCommenterProfile : null}
        onDelete={this.handleDeleteComment}
        onToggleLike={this.handleToggleCommentLike}
        hasTitle={this.state.commentIdsWithTitle.has(c.id)}
        onTitleResolve={(id, has) => {
          this.setState((s) => {
            if (s.commentIdsWithTitle.has(id) === has) return null;
            const next = new Set(s.commentIdsWithTitle);
            if (has) next.add(id); else next.delete(id);
            return { commentIdsWithTitle: next };
          });
        }}
        onCardClick={this.handleCardLinkClick}
        onCardHover={this.handleCardLinkHover}
        onCardHoverEnd={this.handleCardLinkHoverEnd}
        quote={c.quotedText ? (
          <button
            type="button"
            className={`comment-quote-link block w-full text-left text-[0.6cqw] italic whitespace-pre-wrap leading-snug mt-0.5${this.state.pinnedQuote === c.quotedText ? ' is-pinned' : ''}`}
            style={{
              color: PARCHMENT + ' 0.6)',
              paddingLeft: '0.45cqw',
              paddingRight: '0.3cqw',
              paddingTop: '0.1cqw',
              paddingBottom: '0.1cqw',
              borderLeft: `2px solid ${GOLD} 0.5)`,
              borderRadius: '0 0.2cqw 0.2cqw 0',
            }}
            title={t('Show in strategy notes')}
            onMouseEnter={() => this.handleQuoteHover(c.quotedText)}
            onMouseLeave={this.handleQuoteLeave}
            onClick={() => this.handleQuoteClick(c.quotedText)}
          >
            “{c.quotedText.length > 140 ? `${c.quotedText.slice(0, 140).trimEnd()}…` : c.quotedText}”
          </button>
        ) : null}
      />
    );
  }

  renderCommentsColumn() {
    const { currentPlayerId, deckOwnerId } = this.props;
    const {
      comments, commentsLoading, commentDraft, commentPosting,
      commentSort, commentLikeBusy, hoveredCommentId, showSortMenu,
    } = this.state;
    const count = comments.length;

    const sortOptions = [
      { value: 'recent', label: t('Recent') },
      { value: 'liked', label: t('Top liked') },
      { value: 'oldest', label: t('Oldest') },
    ];
    const activeSort = sortOptions.find((s) => s.value === commentSort) || sortOptions[0];

    const accessory = (
      <div className="flex items-center gap-1.5">
        {count > 0 && (
          <span
            className="text-[0.625cqw] font-semibold"
            style={{
              color: PARCHMENT + ' 0.7)',
              background: `${GOLD} 0.12)`,
              border: `1px solid ${GOLD} 0.3)`,
              borderRadius: 'var(--radius-full)',
              padding: '0.05cqw 0.5cqw',
            }}
          >
            {count}
          </span>
        )}
        <div className="relative">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[0.55cqw] transition-colors"
            style={{
              color: TEXT_MUTED,
              padding: '0.18cqw 0.45cqw',
              border: `1px solid ${GOLD} 0.2)`,
              borderRadius: 'var(--radius-sm)',
              background: showSortMenu ? `${GOLD} 0.1)` : 'transparent',
            }}
            onClick={() => this.setState((s) => ({ showSortMenu: !s.showSortMenu }))}
            title={t('Sort comments')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.65cqw', height: '0.65cqw' }}>
              <path d="M3 6h13M3 12h9M3 18h5" />
            </svg>
            <span>{activeSort.label}</span>
          </button>
          {showSortMenu && (
            <>
              <div
                className="fixed inset-0"
                style={{ zIndex: 1 }}
                onClick={() => this.setState({ showSortMenu: false })}
              />
              <div
                className="absolute right-0 top-full mt-1 flex flex-col"
                style={{
                  zIndex: 2,
                  background: '#100d09',
                  border: `1px solid ${GOLD} 0.32)`,
                  borderRadius: 'var(--radius-sm)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.55)',
                  minWidth: '6cqw',
                  padding: '0.2cqw',
                }}
              >
                {sortOptions.map((opt) => {
                  const isActive = opt.value === commentSort;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className="text-left text-[0.625cqw] transition-colors"
                      style={{
                        padding: '0.3cqw 0.5cqw',
                        borderRadius: 'var(--radius-sm)',
                        background: isActive ? `${GOLD} 0.18)` : 'transparent',
                        color: isActive ? TEXT_PRIMARY : TEXT_BODY,
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = `${GOLD} 0.1)`; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                      onClick={() => this.setCommentSort(opt.value)}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    );

    return (
      <div className="flex flex-col flex-1 min-h-0">
        {this.renderColumnHeader(t('Comments'), accessory)}
        <OrnamentalDivider className="mb-3" />

        <div className="flex-1 min-h-0 overflow-y-auto pr-2 -mr-2 pt-4">
          {commentsLoading ? (
            <div className="flex flex-col">
              <CommentCardSkeleton />
              <CommentCardSkeleton />
              <CommentCardSkeleton />
            </div>
          ) : count === 0 ? (
            <div className="text-xs text-center italic py-6" style={{ color: TEXT_MUTED }}>
              {t('No comments yet. Be the first to share a thought.')}
            </div>
          ) : (
            <div className="flex flex-col">
              {comments.map((c, idx) => (
                <Fragment key={c.id}>
                  {this.renderCommentCard(c, {
                    canDelete: c.playerId === currentPlayerId || deckOwnerId === currentPlayerId,
                    isHovered: hoveredCommentId === c.id,
                    isLikeBusy: commentLikeBusy === c.id,
                    continued: continuesPrevious(comments[idx - 1], c),
                  })}
                </Fragment>
              ))}
            </div>
          )}
        </div>

        <div className="pt-3 mt-3">
          <CommentComposer
            value={commentDraft}
            onChange={(v) => this.setState({ commentDraft: v })}
            onSubmit={this.handlePostComment}
            posting={commentPosting}
            sorceryCards={this.props.sorceryCards}
            onOpenCard={this.handleCardLinkClick}
            onCardLinkHover={this.handleCardLinkHover}
            onCardLinkHoverEnd={this.handleCardLinkHoverEnd}
          >
            {this.state.quotedTextDraft ? (
              <div
                className="flex items-start gap-2 mb-2 text-[0.62cqw] italic leading-snug"
                style={{
                  background: 'rgba(180, 140, 60, 0.08)',
                  borderLeft: `2px solid ${GOLD} 0.5)`,
                  borderRadius: '0 0.3cqw 0.3cqw 0',
                  padding: '0.45cqw 0.55cqw',
                  color: PARCHMENT + ' 0.7)',
                }}
              >
                <span className="flex-1 whitespace-pre-wrap overflow-hidden">
                  <span className="not-italic font-semibold mr-1" style={{ color: ACCENT_GOLD, fontSize: '0.55cqw' }}>
                    {t('Replying to')}
                  </span>
                  “{this.state.quotedTextDraft.length > 180 ? `${this.state.quotedTextDraft.slice(0, 180).trimEnd()}…` : this.state.quotedTextDraft}”
                </span>
                <button
                  type="button"
                  className="shrink-0 text-[0.55cqw]"
                  style={{ color: TEXT_MUTED, padding: '0 0.25cqw', background: 'transparent', cursor: 'pointer' }}
                  onClick={this.clearQuotedDraft}
                  title={t('Clear quote')}
                  aria-label={t('Clear quote')}
                >
                  ✕
                </button>
              </div>
            ) : null}
          </CommentComposer>
        </div>
      </div>
    );
  }

  render() {
    if (!this.props.open) return null;
    const { commentsEnabled } = this.props;
    const { entered, exiting } = this.state;
    const visible = entered && !exiting;

    const panelWidth = commentsEnabled ? '70cqw' : '50cqw';
    const panelHeight = commentsEnabled ? '84cqh' : '78cqh';

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: visible
            ? 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.82) 100%)'
            : 'rgba(0,0,0,0)',
          backdropFilter: visible ? 'blur(3px)' : 'blur(0px)',
          opacity: visible ? 1 : 0,
          transition: `opacity ${EXIT_DURATION_MS}ms ease, background ${EXIT_DURATION_MS}ms ease, backdrop-filter ${EXIT_DURATION_MS}ms ease`,
        }}
        onClick={this.beginExit}
      >
        <div
          className="notes-panel-root"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: panelWidth,
            height: panelHeight,
            maxWidth: '94cqw',
            maxHeight: '92cqh',
            padding: '1.4cqw 1.4cqw 1.4cqw 1.4cqw',
            background: `${CONTENT_BG_DEFAULT}, ${NOTES_PANEL_BASE}`,
            backgroundBlendMode: 'overlay, normal',
            border: `1px solid ${GOLD} 0.6)`,
            borderRadius: 'var(--radius-lg)',
            boxShadow: [
              '0 32px 100px rgba(0,0,0,0.7)',
              `0 0 80px ${GOLD} 0.18)`,
              `inset 0 0 0 1px ${GOLD} 0.22)`,
              'inset 0 0 60px rgba(0,0,0,0.55)',
            ].join(', '),
            isolation: 'isolate',
            display: 'flex',
            flexDirection: 'column',
            transform: visible ? 'translateY(0) scale(1)' : 'translateY(1.5cqw) scale(0.96)',
            opacity: visible ? 1 : 0,
            transition: `transform ${ENTRY_DURATION_MS}ms cubic-bezier(0.2, 0.9, 0.25, 1.05) , opacity ${ENTRY_DURATION_MS}ms ease`,
          }}
        >
          <FourCorners color={`${GOLD} 0.65)`} />

          {/* Back button. Top-left corner. Replaces the old close X
              in the top-right; that corner now belongs to the comments
              sidebar so it can extend nearly full-panel height. */}
          <button
            type="button"
            aria-label={t('Back to deck')}
            title={t('Back')}
            data-sound={UI.CANCEL}
            onClick={this.beginExit}
            style={{
              position: 'absolute',
              top: '0.9cqw',
              left: '0.9cqw',
              width: '1.8cqw',
              height: '1.8cqw',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(12, 10, 8, 0.6)',
              border: `1px solid ${GOLD} 0.35)`,
              borderRadius: 'var(--radius-sm)',
              color: TEXT_BODY,
              cursor: 'pointer',
              transition: 'all 150ms ease',
              zIndex: 2,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = TEXT_PRIMARY;
              e.currentTarget.style.borderColor = `${GOLD} 0.6)`;
              e.currentTarget.style.background = `${GOLD} 0.12)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = TEXT_BODY;
              e.currentTarget.style.borderColor = `${GOLD} 0.35)`;
              e.currentTarget.style.background = 'rgba(12, 10, 8, 0.6)';
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '1cqw', height: '1cqw' }}>
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Header inset right + left so it clears the absolute
              back button on the left and the absolute comments
              sidebar on the right. Generous left gutter (3.5cqw)
              gives the deck-identity row room to breathe; the
              previous 1.6cqw had author/elements crammed against
              the back arrow. */}
          <div style={{
            paddingLeft: '3.5cqw',
            paddingRight: commentsEnabled ? '23.5cqw' : 0,
          }}>
            {this.renderHeader()}
          </div>

          {/* Body. Strategy is the primary surface. Right-padding
              reserves the slice the absolute sidebar occupies so
              wrapped text never ducks behind it. */}
          <div
            className="flex-1 min-h-0 flex flex-col"
            style={{
              paddingRight: commentsEnabled ? '23.5cqw' : 0,
            }}
          >
            {this.renderStrategyColumn()}
          </div>

          {/* Comments sidebar. Absolute within the panel (not the
              body) so it reaches from just below the panel's top
              edge to just above its bottom edge. Padding is tight
              so the gap between sidebar and panel border reads as
              a thin gold rule on three sides. */}
          {commentsEnabled && (
            <div
              className="absolute flex flex-col"
              style={{
                top: '0.9cqw',
                right: '0.9cqw',
                bottom: '0.9cqw',
                width: '22cqw',
                background: '#100d09',
                border: `1px solid ${GOLD} 0.35)`,
                borderRadius: 'var(--radius-md)',
                boxShadow: [
                  '-10px 6px 28px rgba(0, 0, 0, 0.55)',
                  '0 4px 12px rgba(0, 0, 0, 0.4)',
                  `inset 0 1px 0 rgba(255, 235, 195, 0.06)`,
                ].join(', '),
                padding: '0.85cqw 0.95cqw 0.95cqw',
                zIndex: 1,
              }}
            >
              {this.renderCommentsColumn()}
            </div>
          )}
        </div>

        {this.renderCardHoverPreview()}
        {this.renderSelectionCommentButton()}
      </div>
    );
  }
}
